import { inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
    combineLatest,
    filter,
    map,
    merge,
    Observable,
    startWith,
    switchMap
} from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KundMediatorService } from "./kund-mediator.service";
import { LeverantorMediatorService } from "./leverantor-mediator.service";

export type Kontaktperson = {
    telefon: string;
    mobil: string;
    epost: string;
};

@Injectable()
export class KontaktpersonMediatorService {

    /**
    * Avtalets kontaktperson. Antingen hämtat från kund (FPV) eller leverantör
    */
    readonly kontaktperson$: Observable<Kontaktperson>;

    /**
     * Finns det en kontakt på avtalet?
     */
    readonly hasKontakt$: Observable<boolean>;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly kundMediatorService = inject(KundMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);

    constructor() {
        const isSkogsvardFPV$ = this.activatedRoute.data.pipe(
            map(data => !!data.isSkogsvardFPV)
        );

        const kundKontaktperson$: Observable<Kontaktperson> = isSkogsvardFPV$.pipe(
            filter(isSkogsvardFPV => isSkogsvardFPV),
            switchMap(() => this.kundMediatorService.kontaktperson$),
            map(k => ({
                epost: k.epost,
                telefon: k.telefon,
                mobil: k.mobil
            }))
        );

        const leverantorKontaktperson$: Observable<Kontaktperson> = this.leverantorMediatorService.leverantorer$.pipe(
            map(leverantorer => leverantorer.find(l => l.kontaktperson)),
            filter(l => ObjectUtils.hasValue(l)),
            map(l => ({
                epost: l.epost,
                telefon: l.telefon,
                mobil: l.kontaktMobiltelefon
            }))
        );

        const hasKund$ = isSkogsvardFPV$.pipe(
            filter(isSkogsvardFPV => isSkogsvardFPV),
            switchMap(() => this.kundMediatorService.kund$),
            map(k => ObjectUtils.hasValue(k)),
            startWith(false)
        );

        const hasLeverantor$ = this.leverantorMediatorService.leverantorer$.pipe(
            map(leverantorer => leverantorer.length > 0)
        );

        this.hasKontakt$ = combineLatest([
            hasLeverantor$,
            hasKund$
        ]).pipe(
            map(([
                lev,
                kund
            ]) =>  lev || kund)
        );

        this.kontaktperson$ = merge(leverantorKontaktperson$, kundKontaktperson$);
    }
}
